import {
  Box,
  Table,
  TableContainer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo, type Ref } from 'react';
import { borderRadius, getModernScrollbar } from '../../../theme/theme';
import { getWorkspaceTableSurfaceSx } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { mappingDensity } from '../../../shared/ui/mapping';
import type { ExportColumn } from '../hooks/useExportColumns';
import { useSystemTableModel } from '../hooks/useSystemTableModel';
import type { PipingSystem } from '../types';
import type { ExportRowHighlightMap } from '../lib/rowHighlights';
import type { ExportFailuresBySystemName } from '../lib/exportFailurePresentation';
import {
  SystemTableDesktopHeader,
  SystemTableMobileHeader,
  SystemTableSelectionSummary,
} from './SystemTableHeader';
import {
  SystemTableDesktopRows,
  SystemTableMobileRows,
} from './SystemTableRows';

interface SystemTableProps {
  readonly systems: PipingSystem[];
  readonly selected: Set<string>;
  readonly onSelectionChange: (selected: Set<string>) => void;
  readonly systemNameFilterValue?: string;
  readonly onSystemNameFilterChange?: (value: string) => void;
  readonly dynamicColumns: ExportColumn[];
  readonly loading?: boolean;
  readonly onStatusClick?: (system: PipingSystem) => void;
  readonly onSelectInModel?: (systems: PipingSystem[]) => void;
  readonly rowHighlightColors?: ExportRowHighlightMap;
  readonly modelSelectionBusy?: boolean;
  readonly selectionDisabled?: boolean;
  readonly systemToolsRef?: Ref<HTMLElement>;
  readonly systemRowsRef?: Ref<HTMLElement>;
  readonly firstSystemRowRef?: Ref<HTMLElement>;
  readonly exportFailuresBySystemName?: ExportFailuresBySystemName;
  readonly onOpenExportLog?: () => void;
}

const EmptyRowHighlightColors: ExportRowHighlightMap = {};
const EmptyExportFailures: ExportFailuresBySystemName = new Map();

export default function SystemTable({
  systems,
  selected,
  onSelectionChange,
  systemNameFilterValue,
  onSystemNameFilterChange,
  dynamicColumns,
  loading,
  onStatusClick,
  onSelectInModel,
  rowHighlightColors = EmptyRowHighlightColors,
  modelSelectionBusy = false,
  selectionDisabled = false,
  systemToolsRef,
  systemRowsRef,
  firstSystemRowRef,
  exportFailuresBySystemName = EmptyExportFailures,
  onOpenExportLog,
}: SystemTableProps) {
  const theme = useTheme();
  const isCompactTable = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrowSystemList = useMediaQuery(theme.breakpoints.down('md'));
  const tableSurfaceSx = useMemo(() => getWorkspaceTableSurfaceSx(theme), [theme]);
  const scrollbarStyles = useMemo(() => getModernScrollbar(theme), [theme]);
  const model = useSystemTableModel({
    systems,
    selected,
    onSelectionChange,
    systemNameFilterValue,
    onSystemNameFilterChange,
    dynamicColumns,
    isCompactTable,
    isNarrowSystemList,
    systemToolsRef,
    systemRowsRef,
    firstSystemRowRef,
  });
  const rowProps = {
    model,
    selected,
    dynamicColumns,
    loading,
    onStatusClick,
    onSelectInModel,
    rowHighlightColors,
    modelSelectionBusy,
    selectionDisabled,
    exportFailuresBySystemName,
    onOpenExportLog,
  };

  if (isNarrowSystemList) {
    return (
      <Box
        data-testid="export-pcf-system-table-root"
        sx={[
          tableSurfaceSx,
          {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            pb: 0,
          },
        ]}
      >
        <SystemTableMobileHeader
          model={model}
          selectionDisabled={selectionDisabled}
          systemToolsRef={systemToolsRef}
        />
        <SystemTableMobileRows {...rowProps} />
      </Box>
    );
  }

  return (
    <Box
      data-testid="export-pcf-system-table-root"
      sx={[
        tableSurfaceSx,
        {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          pb: { xs: 0.25, md: 1 },
          '@media (max-height: 480px)': { pb: 0 },
        },
      ]}
    >
      <SystemTableSelectionSummary model={model} totalSystemCount={systems.length} />
      <TableContainer
        ref={model.setRowsRegionRef}
        data-testid="export-pcf-system-table-scroll-region"
        data-tour-target="export-pcf-system-rows"
        role="region"
        aria-label="Scrollable PCF export systems table"
        aria-busy={Boolean(loading)}
        tabIndex={0}
        onScroll={model.handleTableScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          borderRadius: `${borderRadius.none}px`,
          border: 'none',
          backgroundColor: 'transparent',
          ...scrollbarStyles,
        }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="PCF export systems"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: model.tableMinWidth,
            '& .MuiTableCell-root': {
              py: 0.125,
              px: 1.5,
              fontSize: mappingDensity.tableCellFontSize,
              lineHeight: 1.2,
            },
            '& .MuiTableCell-head': { py: theme.spacing(0.5) },
            '& .MuiTableCell-root:not(:last-child)': {
              borderRight: `1px solid ${alpha(
                theme.palette.divider,
                theme.palette.mode === 'light' ? 0.32 : 0.22,
              )}`,
            },
          }}
        >
          <colgroup>
            <col data-testid="export-pcf-column-selection" style={{ width: model.fixedColumnWidth.selection }} />
            <col data-testid="export-pcf-column-system-name" style={{ width: model.fixedColumnWidth.systemName }} />
            <col data-testid="export-pcf-column-elements" style={{ width: model.fixedColumnWidth.elementCount }} />
            <col data-testid="export-pcf-column-exports" style={{ width: model.fixedColumnWidth.sessionExportCount }} />
            {dynamicColumns.map((column) => (
              <col key={column.id} data-testid={`export-pcf-column-${column.id}`} style={{ width: model.fixedColumnWidth.dynamic }} />
            ))}
            <col data-testid="export-pcf-column-status" style={{ width: model.fixedColumnWidth.status }} />
            <col data-testid="export-pcf-column-model" style={{ width: model.fixedColumnWidth.model }} />
          </colgroup>
          <SystemTableDesktopHeader
            model={model}
            dynamicColumns={dynamicColumns}
            selectionDisabled={selectionDisabled}
          />
          <SystemTableDesktopRows {...rowProps} />
        </Table>
      </TableContainer>
    </Box>
  );
}
